import type { NoticeItemResponse } from '@lmc/ts-common'
import { Card, Heading, Input, RichEditor, theme } from '@lmc/ui-kit'
import { Form, type FormInstance } from 'antd'
import type { Rule } from 'antd/es/form'
import LazyLoad from 'components/routes/LazyLoad'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { noticeboardConstants } from 'locale/noticeboard'
import { useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type NoticeFormProps = {
  onFinish: (values: NoticeFormValues) => void
  form: FormInstance<NoticeFormValues>
  notice?: NoticeItemResponse
  setIsDirty?: (newValue: boolean) => void
}

const NoticeForm = ({
  onFinish,
  form,
  notice,
  setIsDirty
}: NoticeFormProps) => {
  const { t } = useTranslation(NAMESPACE.NOTICEBOARD)

  useEffect(() => {
    form.setFieldsValue({ title: notice?.title || '' })
  }, [form, notice])

  const hasNoTextContent = (html: string) => {
    const plainText = html.replace(/<[^>]*>/g, '').trim()
    return plainText === ''
  }

  const validateHtmlContent = async (_rule: Rule, value: string) => {
    if (hasNoTextContent(value)) {
      throw new Error(t(noticeboardConstants.NO_HTML))
    }
  }

  const validateTitle = async (_rule: Rule, value: string) => {
    if (!value.trim()) {
      throw new Error(
        t(generalConstants.REQUIRED_MESSAGE, { ns: NAMESPACE.GENERAL })
      )
    }
  }

  const color = notice ? 'none' : theme.color.neutral.color10
  const hasMargin = !notice

  return (
    <Wrapper hasMargin={hasMargin}>
      <Form<NoticeFormValues>
        onValuesChange={() => setIsDirty?.(true)}
        onFinish={onFinish}
        form={form}
        initialValues={{ title: notice?.title || '' }}
        data-cy="notice-form-container"
      >
        <NoticeCard color={color}>
          <NoticeCardTitleContainer>
            <Heading level={4}> {t(noticeboardConstants.FORM_TITLE)}</Heading>
          </NoticeCardTitleContainer>
          <NoticeTitle
            name="title"
            rules={[
              {
                required: true,
                message: t(generalConstants.REQUIRED_MESSAGE, {
                  ns: NAMESPACE.GENERAL
                }),
                validator: validateTitle
              }
            ]}
          >
            <Input
              maxLength={100}
              // sorry for inline styling, the input has issues with styled comps
              style={{ width: isMobile ? '100%' : 400 }}
              placeholder={t(generalConstants.START_TYPING, {
                ns: NAMESPACE.GENERAL
              })}
              data-cy="notice-title-input"
            />
          </NoticeTitle>
        </NoticeCard>
        <NoticeCard color={color}>
          <NoticeCardTitleContainer>
            <Heading level={4}> {t(noticeboardConstants.MESSAGE)}</Heading>
          </NoticeCardTitleContainer>
          <NoticeBody
            name="body"
            rules={[
              {
                required: true,
                message: t(generalConstants.REQUIRED_MESSAGE, {
                  ns: NAMESPACE.GENERAL
                })
              },
              { validator: validateHtmlContent }
            ]}
            data-cy="notice-message-input"
          >
            <LazyLoad>
              <RichEditor
                html={notice?.body || ''}
                onChangeHtml={(html) => form.setFieldValue('body', html)}
              />
            </LazyLoad>
          </NoticeBody>
        </NoticeCard>
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ hasMargin: boolean }>`
  max-width: 1080px;
  margin: ${(props) => (props.hasMargin ? '0 auto' : '0')};
`

const NoticeCard = styled(Card)<{ color: string }>`
  padding: 24px;
  margin-bottom: 24px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    padding: 24px 12px;
  }
  background-color: ${(props) => props.color};
`

const NoticeTitle = styled(Form.Item)`
  margin-bottom: 0;
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const NoticeBody = styled(Form.Item)`
  margin-bottom: 0;
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const NoticeCardTitleContainer = styled.div`
  color: ${(props) => props.theme.color.neutral.color4};
  padding-bottom: 24px;
`

export type NoticeFormValues = {
  body: string
  title: string
}

export default NoticeForm
